import { useState } from 'react';
import {
  useInvalidate,
  useNotification,
  useTranslate,
  useUpdate,
} from '@refinedev/core';
import { Modal } from 'antd';
import type {
  PublicationDetailOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { CollectionKind } from '@/api/generated/schemas';
import type { ExcludedField } from '@/pages/user_catalog/publications/excludedIds';

const { confirm } = Modal;

/**
 * Which `excluded_*_ids` array covers a collection kind. Kinds absent from the
 * map cannot be curated from the admin: series, rankings, persons, publishers
 * and emotions have the DB flag but no field on `PublicationUpdateIn`, and
 * `personal` collections belong to their owner, not to us.
 */
const EXCLUDED_FIELD_BY_KIND: Partial<Record<CollectionKind, ExcludedField>> = {
  [CollectionKind.category]: 'excluded_category_ids',
  [CollectionKind.subcategory]: 'excluded_collection_ids',
  [CollectionKind.editorial]: 'excluded_collection_ids',
  [CollectionKind.tag]: 'excluded_custom_tag_ids',
};

interface UseCollectionExclusionProps {
  collectionId: number;
  collectionKind: CollectionKind;
}

interface UseCollectionExclusionResult {
  /** `undefined` when this collection kind cannot hold exclusions. */
  excludedField?: ExcludedField;
  /** Ask for confirmation, then exclude every passed publication. */
  excludeMany: (rows: PublicationListOut[], onSuccess?: () => void) => void;
  isLoading: boolean;
}

/**
 * Exclude publications from a collection. There is no exclusion endpoint —
 * the only write path is `PATCH /user_catalog/publications/{id}` with a full
 * replacement `excluded_*_ids` array, so each publication needs its own
 * request carrying its own list.
 */
export const useCollectionExclusion = ({
  collectionId,
  collectionKind,
}: UseCollectionExclusionProps): UseCollectionExclusionResult => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();
  const { mutateAsync } = useUpdate<PublicationDetailOut>();
  const [isLoading, setIsLoading] = useState(false);

  const excludedField = EXCLUDED_FIELD_BY_KIND[collectionKind];

  const excludeMany = (
    rows: PublicationListOut[],
    onSuccess?: () => void
  ): void => {
    if (!excludedField || !rows.length) return;

    confirm({
      title: t('user_catalog.common.collection_exclusion.confirm', {
        count: rows.length,
      }),
      okText: t('user_catalog.common.collection_exclusion.button'),
      okButtonProps: { danger: true },
      cancelText: t('actions.cancel'),
      onOk: async () => {
        setIsLoading(true);
        const failed: number[] = [];

        // Sequential: every request rewrites one publication's whole list, and
        // a failure halfway through should not drown in parallel noise.
        for (const row of rows) {
          try {
            await mutateAsync({
              resource: 'user_catalog.publications',
              id: row.id,
              values: {
                [excludedField]: [...(row[excludedField] ?? []), collectionId],
              },
              meta: { method: 'patch' },
              // One summary notification instead of one per publication.
              successNotification: false,
            });
          } catch {
            failed.push(row.id);
          }
        }

        setIsLoading(false);

        // Refine invalidates `user_catalog.publications`, never the nested
        // resource this table reads — without this the excluded rows stay.
        invalidate({
          resource: `user_catalog.collections.${collectionId}.publications`,
          invalidates: ['list'],
        });

        const excluded = rows.length - failed.length;
        if (excluded > 0) {
          open?.({
            type: 'success',
            message: t('notifications.success'),
            description: t('user_catalog.common.collection_exclusion.success', {
              count: excluded,
            }),
          });
        }
        if (failed.length) {
          open?.({
            type: 'error',
            message: t('notifications.error'),
            description: t('user_catalog.common.collection_exclusion.error', {
              count: failed.length,
            }),
          });
        }

        onSuccess?.();
      },
    });
  };

  return { excludedField, excludeMany, isLoading };
};
